"use client";

// ChatLinkRows: the places netti hands you, as rows in the dashboard's own list
// language (law_list_language, chat revision 2026-10-15). A folder wears its
// folder icon with its emoji inside, a page or an artifact its glyph, a skill the
// page with its fold turned into a spark, a setting its way there as steps you
// can click (settings › data), a connection its state. The name carries the living-colour underline; the whole row lights
// under the hand. Shared by the dashboard rail (NettiChat) and the Mac app's
// desk rail (Rail.tsx), so it imports only relative, framework-free pieces and
// leaves where a click goes to the caller (onOpen). The list is a bounded
// scroller (five rows and a half, so a sixth peeks): the soft fade at an end
// that has more is read off the scroller here (rowsMore) and drawn by
// netti-rows.css from data-more-top / data-more-bottom.

import * as React from "react";
import DirIcon from "../os/DirIcon";
import type { ChatLink, ChatLinkKind } from "../../lib/netti/links";
import { rowsMore } from "./rowsMore";
import "./netti-rows.css";

/** the glyph a place wears when the server sent none (lib/os/symbols' own marks) */
const GLYPH: Partial<Record<ChatLinkKind, string>> = {
  page: "◍",
  artifact: "❋",
  skill: "✦",
  setting: "◐",
  fact: "⊙",
  activity: "⤴",
  task: "⌖",
  agent: "◉",
  docs: "◌",
  place: "◐",
  web: "◌",
};

/** the quiet words on the right when the server said nothing of where it lives */
const KIND_WORD: Partial<Record<ChatLinkKind, string>> = {
  folder: "folder",
  page: "page",
  artifact: "artifact",
  skill: "skill",
  setting: "settings",
  fact: "about you",
  activity: "activity",
  task: "task",
  agent: "agent",
  docs: "docs",
};

function hostOf(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function metaOf(l: ChatLink): string {
  if (l.meta) return l.meta;
  if (l.kind === "connection") return l.state ?? "connection";
  if (l.kind === "web") return hostOf(l.path);
  return (l.kind && KIND_WORD[l.kind]) ?? "";
}

function Icon({ l }: { l: ChatLink }) {
  if (l.kind === "connection") return <i className="nrow-dot" data-state={l.state ?? "quiet"} aria-hidden />;
  if (l.kind === "folder") return <DirIcon icon="folder" symbol={l.symbol || "⬡"} size={36} color={l.color ?? null} />;
  // a skill wears the page with its fold turned into a spark (DirIcon's skill mark)
  return <DirIcon icon={l.kind === "skill" ? "skill" : "page"} symbol={l.symbol || (l.kind && GLYPH[l.kind]) || "◍"} size={36} color={l.color ?? null} />;
}

type Props = {
  links: ChatLink[];
  /** where a click goes: the dashboard glides the shell, the desk asks main */
  onOpen: (path: string, link: ChatLink) => void;
  /** -1 while the rail is down, so a folded rail never holds the keyboard */
  tabIndex?: number;
};

/** reads the scroller's ends onto it as attributes (never a render per scroll) */
function useRowsMore(ref: React.RefObject<HTMLDivElement | null>, count: number): () => void {
  const sync = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const m = rowsMore(el.scrollTop, el.clientHeight, el.scrollHeight);
    el.toggleAttribute("data-scroll", m.scroll);
    el.toggleAttribute("data-more-top", m.top);
    el.toggleAttribute("data-more-bottom", m.bottom);
  }, [ref]);
  React.useLayoutEffect(() => {
    sync();
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    // the box changes with the rail's width and the rows' own wrapping
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sync, ref, count]);
  return sync;
}

export default function ChatLinkRows({ links, onOpen, tabIndex }: Props): React.JSX.Element | null {
  const ref = React.useRef<HTMLDivElement>(null);
  const onScroll = useRowsMore(ref, links.length);
  if (!links.length) return null;
  return (
    <div ref={ref} className="nrows" role="list" aria-label="places netti found" onScroll={onScroll}>
      {links.map((l, i) => {
        const meta = metaOf(l);
        return (
          <div
            key={`${l.path}-${i}`}
            className="nrow"
            role="listitem"
            data-kind={l.kind}
            data-hl-target=""
            style={{ ["--i" as string]: i } as React.CSSProperties}
          >
            <button type="button" className="nrow-open" tabIndex={tabIndex} onClick={() => onOpen(l.path, l)}>
              <span className="nrow-icon">
                <Icon l={l} />
              </span>
              <span className="nrow-name">{l.label}</span>
              {l.kind === "web" && (
                <svg className="nrow-out" viewBox="0 0 16 16" aria-hidden>
                  <path d="M5 4.5h6.5V11" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              {!l.trail && meta && <span className="nrow-meta">{meta}</span>}
            </button>
            {l.trail && (
              <span className="nrow-trail">
                {l.trail.map((s, j) => (
                  <React.Fragment key={`${s.path}-${j}`}>
                    {j > 0 && <span className="nrow-sep" aria-hidden>›</span>}
                    <button type="button" className="nrow-step" tabIndex={tabIndex} onClick={() => onOpen(s.path, l)}>
                      {s.label}
                    </button>
                  </React.Fragment>
                ))}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
